import type maplibregl from "maplibre-gl";

const defaultLightStyle =
  import.meta.env.VITE_MAP_STYLE_LIGHT ||
  "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";
const defaultDarkStyle =
  import.meta.env.VITE_MAP_STYLE_DARK ||
  import.meta.env.VITE_MAP_STYLE_LIGHT ||
  "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";

export const mapStyles = {
  light: defaultLightStyle,
  dark: defaultDarkStyle,
} as const;

type MapOptions = Omit<maplibregl.MapOptions, "container"> & {
  container: string | HTMLElement;
  snapToIntegerZoom?: boolean;
};

const baseOptions: Omit<maplibregl.MapOptions, "container"> = {
  style: mapStyles.light,
  attributionControl: false,
  fadeDuration: 300,
  cooperativeGestures: false,
  center: [122, 13],
  zoom: 7,
  canvasContextAttributes: {
    preserveDrawingBuffer: true,
  },
};

export const mapDefaults = {
  style: mapStyles.light,
};

export function createBaseMap(
  maplibre: typeof maplibregl,
  options: MapOptions,
): maplibregl.Map {
  const { snapToIntegerZoom = false, ...rest } = options;
  const map = new maplibre.Map({
    ...baseOptions,
    ...rest,
    maxZoom: options.maxZoom ?? 21,
    canvasContextAttributes: {
      ...baseOptions.canvasContextAttributes,
      ...(rest.canvasContextAttributes ?? {}),
    },
  });

  if (snapToIntegerZoom) {
    let snapping = false;
    const handleWheel = (event: maplibregl.MapWheelEvent) => {
      event.preventDefault();
      const delta = event.originalEvent.deltaY;
      const direction = delta > 0 ? -1 : 1; // wheel down -> zoom out
      const current = map.getZoom();
      const next = Math.round(current) + direction;
      map.easeTo({ zoom: next, duration: 0 });
    };
    const handleSnap = () => {
      if (snapping) {
        snapping = false;
        return;
      }
      const current = map.getZoom();
      const snapped = Math.round(current);
      if (Math.abs(snapped - current) > 0.01) {
        snapping = true;
        map.easeTo({ zoom: snapped, duration: 0 });
      }
    };
    map.on("zoomend", handleSnap);
    map.on("wheel", handleWheel);
  }

  // Prevent page scrolling when the pointer is over the map canvas while still allowing zoom.
  const canvas = map.getCanvas();
  const stopScroll = (event: WheelEvent) => {
    event.preventDefault();
  };
  canvas.addEventListener("wheel", stopScroll, { passive: false });
  map.on("remove", () => {
    canvas.removeEventListener("wheel", stopScroll);
  });

  return map;
}
